import React from 'react';
import { Input } from '../../../shared/components/Input';
import { Select } from '../../../shared/components/Select';
import { Checkbox } from '../../../shared/components/Checkbox';
import { PlatformRatesDrawer, PLATFORM_RATE_FALLBACKS } from './PlatformRatesDrawer';

/**
 * Detail pane for the project-level Platform Subscription rail item. Sets the
 * options that drive the platform fee and retention (number of users, retention
 * tier and years, customer-managed cloud), shows the live platform-monthly
 * build-up, and opens a side drawer to edit the underlying platform rates. These
 * are project-level (one set per quote), not per module.
 */

export interface PlatformOptions {
  numberOfUsers: number;
  retentionTier: 'cold' | 'warm' | null;
  retentionYears: number;
  customerManagedCloud: boolean;
}

interface Props {
  included: boolean;
  onIncludedChange: (included: boolean) => void;
  options: PlatformOptions;
  onChange: (patch: Partial<PlatformOptions>) => void;
  // The last Calculate Price project-level total, shown for reference.
  total?: number;
  // The active platform config rates and the per-proposal overrides on them.
  platformConfig?: Record<string, number>;
  platformOverrides?: Record<string, number>;
  onPlatformOverrideChange?: (key: string, value: number | undefined) => void;
  // Retention annual fees, project months, and the duration discount, so the
  // build-up can show the period total and retention live.
  retentionConfig?: Record<string, number>;
  retentionOverrides?: Record<string, number>;
  onRetentionOverrideChange?: (key: string, value: number | undefined) => void;
  months?: number;
  discountPct?: number;
}

const RETENTION_FALLBACKS: Record<string, number> = {
  coldArchiveAnnualFee: 3000,
  warmAccessAnnualFee: 15000,
};

const RETENTION_OPTIONS = [
  { value: 'none', label: 'No retention' },
  { value: 'cold', label: 'Cold archive (low cost)' },
  { value: 'warm', label: 'Warm access (higher cost)' },
];

const RATE_KEYS = Object.keys(PLATFORM_RATE_FALLBACKS);
const RETENTION_KEYS = Object.keys(RETENTION_FALLBACKS);

const formatCurrency = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPercent = (fraction: number): string => `${Math.round(fraction * 100)}%`;

const trimNum = (n: number): string =>
  Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);

interface BuildupStep {
  label: string;
  calc?: string;
  value: string;
  kind?: 'cost' | 'total';
}

const BuildupTable: React.FC<{ steps: BuildupStep[] }> = ({ steps }) => (
  <div className="mt-3 space-y-px rounded-md border border-[#E2E5EA] bg-page-bg px-3 py-2">
    {steps.map((step, i) => {
      const emphasised = step.kind === 'cost' || step.kind === 'total';
      return (
        <div
          key={i}
          className={`grid grid-cols-[7rem_1fr_auto] gap-x-3 text-xs ${
            emphasised
              ? 'mt-1 border-t border-[#E2E5EA] pt-1 font-medium text-text-grey'
              : 'text-heritage-grey'
          }`}
        >
          <span>{step.label}</span>
          <span className="text-heritage-grey">{step.calc ?? ''}</span>
          <span className="text-right tabular-nums">{step.value}</span>
        </div>
      );
    })}
  </div>
);

export const PlatformOptionsPane: React.FC<Props> = ({
  included,
  onIncludedChange,
  options,
  onChange,
  total,
  platformConfig,
  platformOverrides,
  onPlatformOverrideChange,
  retentionConfig,
  retentionOverrides,
  onRetentionOverrideChange,
  months = 0,
  discountPct = 0,
}) => {
  const [ratesDrawerOpen, setRatesDrawerOpen] = React.useState(false);

  const tierValue = options.retentionTier ?? 'none';
  const hasRetention = options.retentionTier !== null;

  const overrides = platformOverrides ?? {};
  const overrideCount = Object.keys(overrides).length;
  const rateValue = (key: string): number =>
    overrides[key] ?? platformConfig?.[key] ?? PLATFORM_RATE_FALLBACKS[key] ?? 0;

  // Live platform-monthly build-up from the effective rates, mirroring the
  // backend: (infra + hours x rate) / (1 - margin), plus per-user x users.
  const infra = options.customerManagedCloud ? 0 : rateValue('baseInfraMonthly');
  const supportHours = rateValue('baseSupportHours');
  const supportRate = rateValue('supportHourlyRate');
  const supportCost = supportHours * supportRate;
  const margin = rateValue('marginTarget');
  const monthlyCost = infra + supportCost;
  const baseFee = monthlyCost / (1 - margin);
  const perUserRate = rateValue('perUserMonthly');
  const users = options.numberOfUsers || 0;
  const perUserFee = perUserRate * users;
  const platformMonthly = baseFee + perUserFee;

  // Period total: platform fee across the months, less the duration discount.
  // Retention is value-set per year and is not discounted.
  const platformPeriod = platformMonthly * months * (1 - discountPct / 100);
  const retentionOverridesObj = retentionOverrides ?? {};
  const retentionFee = (key: string): number =>
    retentionOverridesObj[key] ?? retentionConfig?.[key] ?? RETENTION_FALLBACKS[key] ?? 0;
  const retentionAnnual =
    options.retentionTier === 'warm'
      ? retentionFee('warmAccessAnnualFee')
      : options.retentionTier === 'cold'
        ? retentionFee('coldArchiveAnnualFee')
        : 0;
  const retentionTotal = retentionAnnual * (options.retentionYears || 0);
  const periodTotal = platformPeriod + retentionTotal;

  const monthsLabel = `x ${trimNum(months)} mo${discountPct > 0 ? `, less ${formatPercent(discountPct / 100)}` : ''}`;

  const steps: BuildupStep[] = [
    {
      label: 'Infra',
      calc: options.customerManagedCloud ? 'customer-managed (dropped)' : 'fixed / mo',
      value: formatCurrency(infra),
    },
    {
      label: 'Support',
      calc: `${trimNum(supportHours)} hrs x ${formatCurrency(supportRate)}/hr`,
      value: formatCurrency(supportCost),
    },
    { label: 'Cost / mo', value: formatCurrency(monthlyCost), kind: 'cost' },
    {
      label: 'Base fee',
      calc: `cost / (1 - ${formatPercent(margin)} margin)`,
      value: formatCurrency(baseFee),
    },
    ...(users > 0
      ? [
          {
            label: 'Per-user',
            calc: `${formatCurrency(perUserRate)} x ${users} users`,
            value: formatCurrency(perUserFee),
          },
        ]
      : []),
    { label: 'Platform / mo', value: formatCurrency(platformMonthly) },
    { label: 'Platform total', calc: monthsLabel, value: formatCurrency(platformPeriod), kind: 'cost' },
    ...(retentionTotal > 0
      ? [
          {
            label: 'Retention',
            calc: `${options.retentionTier} ${formatCurrency(retentionAnnual)}/yr x ${options.retentionYears} yrs`,
            value: formatCurrency(retentionTotal),
          },
        ]
      : []),
    { label: 'Total for period', value: formatCurrency(periodTotal), kind: 'total' as const },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold font-heading text-blueprint-blue">Platform Subscription</h2>
        <p className="mt-1 text-sm text-heritage-grey">
          The recurring platform fee plus optional post-handover data retention, charged once at the
          project level across all selected modules.
        </p>
      </div>

      <Checkbox
        id="platform-included"
        label="Include platform subscription and retention in this quote"
        tooltip="Uncheck to exclude the recurring platform fee and retention entirely."
        checked={included}
        onChange={(e) => onIncludedChange(e.target.checked)}
      />

      <div className={included ? 'space-y-5' : 'space-y-5 pointer-events-none opacity-50'}>
        <div className="w-56">
          <Input
            id="platform-users"
            label="Number of users"
            tooltip="Drives the per-user portion of the platform fee."
            type="number"
            min={0}
            value={options.numberOfUsers || ''}
            onChange={(e) =>
              onChange({ numberOfUsers: Math.max(0, Number(e.target.value) || 0) })
            }
            placeholder="0"
          />
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="w-64">
            <Select
              label="Data retention"
              tooltip="Post-handover archival of the project's records, billed per year."
              value={tierValue}
              options={RETENTION_OPTIONS}
              onChange={(value) =>
                onChange({ retentionTier: value === 'none' ? null : (value as 'cold' | 'warm') })
              }
            />
          </div>
          <div className="w-40">
            <Input
              id="platform-retention-years"
              label="Retention years"
              tooltip="Years of post-handover retention. Applies only when a tier is selected."
              type="number"
              min={0}
              value={hasRetention ? options.retentionYears || '' : ''}
              disabled={!hasRetention}
              onChange={(e) =>
                onChange({ retentionYears: Math.max(0, Number(e.target.value) || 0) })
              }
              placeholder="0"
            />
          </div>
        </div>

        <Checkbox
          id="customer-managed-cloud"
          label="Customer-managed cloud"
          tooltip="The customer hosts the infrastructure, so the platform fee drops its infra portion."
          checked={options.customerManagedCloud}
          onChange={(e) => onChange({ customerManagedCloud: e.target.checked })}
        />

        <div className="border-t border-[#E2E5EA] pt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-blueprint-blue">Platform fee build-up</h3>
            <button
              type="button"
              onClick={() => setRatesDrawerOpen(true)}
              className="rounded-lg border border-[#E2E5EA] px-3 py-1.5 text-sm font-medium text-text-grey hover:bg-page-bg"
            >
              Edit platform rates
              {overrideCount > 0 && (
                <span className="ml-1.5 rounded-full bg-primary px-1.5 py-0.5 text-xs text-white">
                  {overrideCount}
                </span>
              )}
            </button>
          </div>
          <BuildupTable steps={steps} />
          <p className="mt-1.5 text-xs text-heritage-grey">
            Period total uses the project duration ({trimNum(months)} months) and its discount band. The
            platform fee is discounted; retention is not.
          </p>
        </div>
      </div>

      <div className="border-t border-[#E2E5EA] pt-4 text-sm">
        {!included ? (
          <p className="text-heritage-grey">Platform subscription is excluded from this quote.</p>
        ) : total && total > 0 ? (
          <p className="text-text-grey">
            Platform &amp; retention (last calculated):{' '}
            <span className="font-semibold text-blueprint-blue">{formatCurrency(total)}</span>
          </p>
        ) : (
          <p className="text-heritage-grey">Click Calculate Price to see the platform total.</p>
        )}
      </div>

      <PlatformRatesDrawer
        open={ratesDrawerOpen}
        onClose={() => setRatesDrawerOpen(false)}
        config={platformConfig}
        overrides={overrides}
        onOverrideChange={(key, value) => onPlatformOverrideChange?.(key, value)}
        retentionConfig={retentionConfig}
        retentionOverrides={retentionOverridesObj}
        onRetentionOverrideChange={(key, value) => onRetentionOverrideChange?.(key, value)}
        onResetAll={() => {
          RATE_KEYS.forEach((key) => onPlatformOverrideChange?.(key, undefined));
          RETENTION_KEYS.forEach((key) => onRetentionOverrideChange?.(key, undefined));
        }}
      />
    </div>
  );
};
